const PRESERVE_KEYS = ['arcm_trust_browser_token'];

const AUTH_STORAGE_KEYS = new Set([
    'IdToken',
    'RefreshToken',
    'AccessToken',
    'email',
    'user_id',
    'id',
    'user_type',
    'session_token',
    'custom:is_first_time_login',
    'user-info',
    'browser_trust_duration',
    'custom-status',
    'verify_otp_session_token',
    'preference_id',
    'token',
]);

const LOGOUT_EVENT_KEY = 'arcm:logout-event';
const FORCED_LOGOUT_EVENT_KEY = 'arcm:forced-logout-event';
const LOGIN_EVENT_KEY = 'arcm:login-event';

// Session cookie: no Max-Age/Expires => dropped when browser closes. Used to detect new browser session.
const SESSION_COOKIE_NAME = 'arcm_browser_session';

const getSessionCookie = () => {
    if (typeof document === 'undefined' || !document.cookie) return false;
    return document.cookie.split(';').some((part) =>
        part.trim().startsWith(SESSION_COOKIE_NAME + '=')
    );
};

const setSessionCookie = () => {
    if (typeof document === 'undefined') return;
    try {
        document.cookie = `${SESSION_COOKIE_NAME}=1; path=/; SameSite=Lax`;
    } catch (error) {
        console.error('Error setting session cookie:', error);
    }
};

const clearSessionCookie = () => {
    if (typeof document === 'undefined') return;
    try {
        document.cookie = `${SESSION_COOKIE_NAME}=; path=/; Max-Age=0`;
    } catch (error) {
        console.error('Error clearing session cookie:', error);
    }
};

/**
 * Call once before the app renders. If the session cookie is missing (browser was closed and reopened),
 * clear auth storage so the app does not use stale tokens. Preserves non-auth keys (e.g. arcm_trust_browser_token).
 * Also clears duplicate_session_pending flag to ensure cross-tab reload syncs properly.
 */
export const clearStorageIfNewBrowserSession = () => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const sess = getStorage('sessionStorage');
    if (sess) {
        removeItem(sess, 'duplicate_session_pending');
    }
    if (!getSessionCookie()) {
        clearAuthSession();
    }
};

const getStorage = (type) => {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        return window[type];
    } catch (error) {
        console.error(`Unable to access ${type}:`, error);
        return null;
    }
};

const session = getStorage('sessionStorage');
const local = getStorage('localStorage');

const serialize = (value) => {
    if (value === undefined || value === null) {
        return null;
    }

    if (typeof value === 'string') {
        return value;
    }

    try {
        return JSON.stringify(value);
    } catch (error) {
        console.warn('Failed to serialise value, storing as string', error);
        return String(value);
    }
};

const setItem = (storage, key, value) => {
    if (!storage || value === null) {
        return;
    }
    try {
        storage.setItem(key, value);
    } catch (error) {
        console.error(`Unable to set ${key} in storage:`, error);
    }
};

const removeItem = (storage, key) => {
    if (!storage) {
        return;
    }
    try {
        storage.removeItem(key);
    } catch (error) {
        console.error(`Unable to remove ${key} from storage:`, error);
    }
};

const readItem = (storage, key) => {
    if (!storage) {
        return null;
    }
    try {
        return storage.getItem(key);
    } catch (error) {
        console.error(`Unable to read ${key} from storage:`, error);
        return null;
    }
};

export const setAuthItem = (key, value) => {
    const serialisedValue = serialize(value);
    if (serialisedValue === null) {
        return;
    }

    AUTH_STORAGE_KEYS.add(key);
    setItem(session, key, serialisedValue);
    setItem(local, key, serialisedValue);
};

export const getAuthItem = (key) => {
    const sessionValue = readItem(session, key);
    if (sessionValue !== null && sessionValue !== undefined && sessionValue !== '') {
        return sessionValue;
    }
    return readItem(local, key);
};

export const removeAuthItem = (key) => {
    removeItem(session, key);
    removeItem(local, key);
};

export const persistAuthSession = (entries) => {
    if (!entries || typeof entries !== 'object') {
        return;
    }
    Object.entries(entries).forEach(([key, value]) => {
        setAuthItem(key, value);
    });
    // Promote staged session to fully persisted — clear pending flag if present
    removeItem(session, 'duplicate_session_pending');
    setSessionCookie();
};

// Stage auth session to sessionStorage only (not localStorage).
// New tabs cannot read tokens from localStorage and bypass the duplicate session modal.
// Sets duplicate_session_pending so the router blocks authenticated routes.
export const stageAuthSession = (entries) => {
    if (!entries || typeof entries !== 'object') {
        return;
    }
    Object.entries(entries).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
            setItem(session, key, String(value));
        }
    });
    setItem(session, 'duplicate_session_pending', 'true');
    setSessionCookie();
};

export const syncSessionFromLocalStorage = () => {
    if (!local || !session) {
        return;
    }

    AUTH_STORAGE_KEYS.forEach((key) => {
        const hasSessionValue = readItem(session, key);
        if (hasSessionValue === null || hasSessionValue === undefined || hasSessionValue === '') {
            const localValue = readItem(local, key);
            if (localValue !== null && localValue !== undefined) {
                setItem(session, key, localValue);
            }
        }
    });
};

export const clearAuthSession = () => {
    const preservedData = {};
    PRESERVE_KEYS.forEach((key) => {
        const value = readItem(local, key);
        if (value !== null) {
            preservedData[key] = value;
        }
    });

    AUTH_STORAGE_KEYS.forEach((key) => {
        removeAuthItem(key);
    });
    removeItem(session, 'duplicate_session_pending');
    clearSessionCookie();

    Object.keys(preservedData).forEach((key) => {
        setItem(local, key, preservedData[key]);
    });
};

export const broadcastLogout = () => {
    setItem(local, LOGOUT_EVENT_KEY, Date.now().toString());
};

// Sibling old-session tabs receive this and show the "another login session" toast
// instead of the "logged out in another tab" toast.
export const broadcastForcedLogout = () => {
    setItem(local, FORCED_LOGOUT_EVENT_KEY, Date.now().toString());
};

export const broadcastLogin = () => {
    setItem(local, LOGIN_EVENT_KEY, Date.now().toString());
};

export const subscribeToAuthEvents = (handler) => {
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
        return () => {};
    }

    const listener = (event) => {
        if (event.key === LOGOUT_EVENT_KEY && event.newValue) {
            handler('logout');
        }
        if (event.key === FORCED_LOGOUT_EVENT_KEY && event.newValue) {
            handler('forced_logout');
        }
        if (event.key === LOGIN_EVENT_KEY && event.newValue) {
            handler('login');
        }
    };

    window.addEventListener('storage', listener);
    return () => window.removeEventListener('storage', listener);
};


